import React from 'react';
import { Check, ArrowRight, Gift, Users, Clock, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSiteContent } from '../context/SiteContentContext';
import { useLanguage } from '../context/LanguageContext';

const PlanCard = ({ plan, language, isPopular = false, translations = {} }) => {
  // Content is already localized by SiteContentContext
  const name = plan.name || '';
  const tagline = plan.tagline || '';
  const duration = plan.duration || '';
  const features = plan.features || [];

  // Provide defaults for translations
  const t = {
    mostPopular: translations.mostPopular || 'Most Popular',
    classesPerMonth: translations.classesPerMonth || 'classes/month',
    getStarted: translations.getStarted || 'Get Started',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`relative bg-white rounded-2xl p-8 border flex flex-col h-full ${
        isPopular ? 'border-amber-400 shadow-xl shadow-amber-100/50' : 'border-slate-200 shadow-lg shadow-slate-900/5'
      }`}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-amber-400 text-amber-900 text-sm font-medium px-4 py-1 rounded-full">
            {t.mostPopular}
          </span>
        </div>
      )}
      
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-slate-900 mb-2">{name}</h3>
        <p className="text-slate-600 text-sm">{tagline}</p>
      </div>

      <div className="text-center mb-6 pb-6 border-b border-slate-100">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-4xl font-bold text-slate-900">{plan.classes}</span>
          <span className="text-slate-600">{t.classesPerMonth}</span>
        </div>
        <p className="text-sm text-slate-500 mt-2">{duration}</p>
      </div>

      <ul className="space-y-3 mb-8 flex-grow">
        {features.map((feature, idx) => {
          // Feature is already localized by SiteContentContext
          const featureText = typeof feature === 'string' ? feature : (feature[language] || feature.en || '');
          const featureKey = `${name}-feature-${idx}`;
          return (
            <li key={featureKey} className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-slate-700 text-sm">
                {featureText}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="mt-auto">
        <Link
          to="/contact"
          data-testid={`plan-get-started-${plan.classes}`}
          className={`w-full flex items-center justify-center gap-2 py-3 px-6 rounded-full font-medium transition-all ${
            isPopular
              ? 'bg-amber-400 text-amber-900 hover:bg-amber-500'
              : 'bg-slate-900 text-white hover:bg-slate-800'
          }`}
        >
          {t.getStarted}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.div>
  );
};

const Pricing = () => {
  const { siteContent } = useSiteContent();
  const { language } = useLanguage();
  const pricingPage = siteContent?.pricingPage || {};
  const plans = pricingPage.plans || [];
  const inclusions = pricingPage.inclusions || {};
  const discounts = pricingPage.discounts || {};

  // Content is already localized by SiteContentContext
  const headerLabel = pricingPage.headerLabel || 'Pricing';
  const headerTitle = pricingPage.headerTitle || 'Flexible Learning Plans';
  const headerSubtitle = pricingPage.headerSubtitle || '';
  const inclusionsTitle = inclusions.title || 'All Plans Include';
  const discountsTitle = discounts.title || 'Discounts & Promotions';
  const ctaText = pricingPage.ctaText || '';

  // Translated UI strings
  const translations = {
    mostPopular: pricingPage.mostPopularLabel || 'Most Popular',
    classesPerMonth: pricingPage.classesPerMonthLabel || 'classes/month',
    getStarted: pricingPage.getStartedLabel || 'Get Started',
    readyToStart: pricingPage.ctaSectionTitle || 'Ready to Start Learning?',
    readyToStartSubtitle: pricingPage.ctaSectionSubtitle || 'Contact us today to discuss which plan is right for you.',
    contactUs: pricingPage.ctaSectionButton || 'Contact Us',
  };

  return (
    <div data-testid="pricing-page">
      {/* Header */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4 block">
              {headerLabel}
            </span>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
              {headerTitle}
            </h1>
            <p className="text-lg text-slate-600">
              {headerSubtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => {
              const planKey = plan.name?.en || `plan-${index}`;
              return (
                <PlanCard 
                  key={planKey} 
                  plan={plan} 
                  language={language}
                  isPopular={index === 1}
                  translations={translations}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* Inclusions */}
      {inclusions.items && (
        <section className="py-20 md:py-28 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="w-14 h-14 bg-slate-200/60 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-6 h-6 text-slate-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                {inclusionsTitle}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {inclusions.items.map((item, index) => {
                // Item is already localized by SiteContentContext
                const itemText = typeof item === 'string' ? item : (item[language] || item.en || '');
                const itemKey = `inclusion-${index}`;
                return (
                  <motion.div
                    key={itemKey}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-100"
                  >
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700">
                      {itemText}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Discounts */}
      {discounts.items && (
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Gift className="w-6 h-6 text-amber-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                {discountsTitle}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {discounts.items.map((item, index) => {
                // Item is already localized by SiteContentContext
                const itemText = typeof item === 'string' ? item : (item[language] || item.en || '');
                const itemKey = `discount-${index}`;
                return (
                  <motion.div
                    key={itemKey}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-center gap-3 bg-amber-50 p-4 rounded-xl border border-amber-100"
                  >
                    <Gift className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span className="text-slate-700">
                      {itemText}
                    </span>
                  </motion.div>
                );
              })}
            </div>

            {ctaText && (
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-center text-slate-600 mt-8"
              >
                {ctaText}
              </motion.p>
            )}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
              {translations.readyToStart}
            </h2>
            <p className="text-slate-600 mb-8 max-w-xl mx-auto">
              {translations.readyToStartSubtitle}
            </p>
            <Link
              to="/contact"
              data-testid="pricing-cta-button"
              className="inline-flex items-center justify-center bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-3.5 font-medium transition-all"
            >
              {translations.contactUs}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
